import './PageNameCellRenderer.scss';

import { useAurigaContext } from '@context/AurigaContext';
import { useDeckContext } from '@context/DeckContext';
import { useTOCContext } from '@context/TOCContext';
import { Box } from '@mui/material';
import Link from '@mui/material/Link';
import { changeEntryField, getModifiedFields } from '@utils/deckManagementEntityHelpers';
import { isNil } from 'lodash';
import { useCallback, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { CellValueChangeParams, EditableCellParams, PageNameCellRendererProps } from 'types/TOC.types';
import { EnhancedTOCEntry } from 'types/TOCContext.types';

import EditableCellRenderer from './EditableCellRenderer';

const PageNameCellRenderer = (params: PageNameCellRendererProps) => {
  const { isEditing, onEndEdit, onCellValueChange, deckId } = params;
  const { isPageConfigLoading, isPageConfigError, pageIdToLayoutFamilyMap } = useTOCContext();
  const { deckLockStatus } = useDeckContext();
  const { toastNotification } = useAurigaContext();
  const editableParams: EditableCellParams = useMemo(
    () => ({
      data: params.data!,
      value: params.value,
      field: params.column?.getColId() || 'page_name',
      api: params.api,
    }),
    [params.api, params.column, params.data, params.value],
  );

  /** Checks if it's safe to navigate to the page.
   *
   * Page navigation is allowed only when:
   * 1. Deck Column data has loaded
   * 2. `params.data` is defined
   * 3. `params.data.page_name` is defined
   * 4. `page_name` has NOT been modified (i.e., it's not in the modified fields list)
   * 5. `params.data.page_id` is defined
   *
   * @returns boolean indicating if navigation is allowed
   */
  const canNavigateToPage = useCallback(
    (data?: EnhancedTOCEntry): data is EnhancedTOCEntry & { page_id: string; page_name: string } => {
      return (
        !isPageConfigLoading &&
        !isNil(data) &&
        // Page Name Validations
        !isNil(data.page_name) &&
        !getModifiedFields(data).includes('page_name') &&
        // Page ID Validations
        !isNil(data.page_id)
      );
    },
    [isPageConfigLoading],
  );

  /**
   * Handles cell value updates and shows a toast notification if the page name is modified.
   *
   * @param onChangeParams - Parameters containing information about the cell value change.
   */
  const handleUpdateCellValue = useCallback(
    (onChangeParams: CellValueChangeParams) => {
      onCellValueChange(onChangeParams);

      // Simulates a modification to determine if the field has been modified (or renamed back to original value).
      const testModify = changeEntryField(onChangeParams.data, onChangeParams.field, onChangeParams.newValue);
      if (getModifiedFields(testModify).includes('page_name')) {
        // Display toast notification if the page_name value has been modified.
        toastNotification({
          message: 'Page Name updated. To access the renamed page, Save Changes is required',
          visible: true,
          type: 'success',
        });
      }
    },
    [onCellValueChange, toastNotification],
  );

  /**
   * Generates the URL for navigating to a page based on various conditions.
   *
   * The resulting URL depends on:
   * 1. If page configuration is still loading (returns undefined, since the layout family map is not yet reliable)
   * 2. If the cell is in edit mode (returns undefined to prevent navigation)
   * 3. If the page has a layout family assigned (navigates to the page using 'page_name')
   * 4. If the page has no layout family and the deck is locked by the current user (navigates to Page Setup Wizard)
   * 5. If the page has no layout family and the deck is NOT locked by the current user (returns undefined to prevent navigation)
   *
   * @returns The URL for navigating to the page or undefined if navigation is not allowed
   */
  const pageUrl = useMemo(() => {
    if (isPageConfigLoading) {
      // No URL while PageConfig is still loading
      return;
    }

    if (!isEditing(editableParams)) {
      // Don't open dialog for new rows
      if (params.data?.metadata?.isNewRow || !canNavigateToPage(params.data)) {
        return;
      }

      if (pageIdToLayoutFamilyMap.has(params.data.page_id)) {
        // If the page has an assigned layout family, redirect to the page using 'page_name' attribute (for PageName based routing)
        return `/deck-preparation/decks/${deckId}/pages/${encodeURIComponent(params.data.page_name)}`;
      } else if (!isPageConfigError && !deckLockStatus.isEditingDisabled) {
        // If the page has no layout family (and its config loaded successfully) and the deck is
        // locked by the current user, redirect to Page Setup Wizard. On a config load error we
        // avoid routing into setup, since the page may actually be configured.
        return `/deck-preparation/decks/${deckId}/page-setup/${encodeURIComponent(params.data.page_name)}`;
      }
    }
    return undefined;
  }, [
    canNavigateToPage,
    deckId,
    deckLockStatus.isEditingDisabled,
    editableParams,
    isEditing,
    isPageConfigError,
    isPageConfigLoading,
    pageIdToLayoutFamilyMap,
    params.data,
  ]);

  /**
   * Renders clickable content (a Link) for the cell.
   * When a page URL is available, the Link is rendered as a react-router Link so plain clicks navigate
   * client-side, while middle-click / modifier-click still open a new tab via the native href.
   * When no URL is available (e.g. while page configuration is loading), the Link has no href and is
   * rendered as disabled.
   * @see {pageUrl}
   * @returns A Link component that navigates to the page URL, or a disabled Link if no URL is available
   */
  const renderClickableContent = useCallback(() => {
    const className = `page-name-link ${isPageConfigLoading ? 'disabled' : ''}`;
    if (isNil(pageUrl)) {
      return (
        <Link className={className} underline='none'>
          {params.value}
        </Link>
      );
    }
    return (
      <Link component={RouterLink} to={pageUrl} className={className} underline='none'>
        {params.value}
      </Link>
    );
  }, [isPageConfigLoading, pageUrl, params.value]);

  /**
   * Renders static content (plain text) for the cell.
   * When the page needs setup but the deck is not locked, displays a span tooltip text on hover
   * informing the user they must lock the deck to configure the page. When page configuration
   * failed to load, displays a load-failure tooltip (the cell is intentionally non-interactive).
   *
   * @param needsPageSetup - Whether the page requires initial setup (no template configured)
   * @param configError - Whether the page's configuration failed to load
   */
  const renderStaticContent = useCallback(
    (needsPageSetup: boolean, configError = false) => {
      const title = configError
        ? 'Unable to load page configuration. Please refresh and try again.'
        : needsPageSetup
          ? 'Lock Deck to set up this page'
          : undefined;
      return (
        <Box component='span' className={needsPageSetup ? 'page-name-needs-setup' : ''} title={title}>
          {params.value}
        </Box>
      );
    },
    [params.value],
  );

  if (isEditing(editableParams)) {
    return (
      <EditableCellRenderer
        {...params}
        column={params.column!}
        isEditing={isEditing}
        onEndEdit={onEndEdit}
        onCellValueChange={handleUpdateCellValue}
      />
    );
  }

  // If the component is in "loading" state, or if the page name is valid and can be navigated to, render clickable content; otherwise, render static content.
  // Pages needing setup are only clickable when the deck is locked by the current user (i.e. editing enabled).
  // When page configuration failed to load (isPageConfigError), do NOT classify a page as needing setup —
  // absence from the map may be a transient fetch failure, not a genuinely unconfigured page.
  const needsPageSetup =
    canNavigateToPage(params.data) && !isPageConfigError && !pageIdToLayoutFamilyMap.has(params.data.page_id);
  // A page's config failed to load: render non-interactive static content with a load-failure
  // tooltip rather than a dead clickable element (it is neither navigable nor safe to route to setup).
  const hasConfiguredLayout = canNavigateToPage(params.data) && pageIdToLayoutFamilyMap.has(params.data.page_id);
  if (isPageConfigError && !hasConfiguredLayout) {
    return renderStaticContent(false, true);
  }

  return isPageConfigLoading ||
    (canNavigateToPage(params.data) && (!needsPageSetup || !deckLockStatus.isEditingDisabled))
    ? renderClickableContent()
    : renderStaticContent(needsPageSetup);
};

export default PageNameCellRenderer;
